// Extracting a YouTube video ID from user input. Environment-neutral (browser + Node).

const ID_RE = /^[A-Za-z0-9_-]{11}$/;
/** An ID at the start of a string, not followed by further ID characters ("dQw4w9WgXcQ&t=5" -> "dQw4w9WgXcQ"). */
const LEADING_ID_RE = /^([A-Za-z0-9_-]{11})(?![A-Za-z0-9_-])/;
/** 11-character path words that are not video IDs (/embed/videoseries?list=..., /embed/live_stream?channel=...). */
const NOT_IDS = new Set(['videoseries', 'live_stream']);

/** Path prefixes whose next segment is the video ID: /shorts/<id>, /embed/<id>, ... */
const ID_PATH_PREFIXES = new Set(['watch', 'shorts', 'live', 'embed', 'v', 'e', 'vi', 'vi_webp']);

/** Query parameters that may carry a nested URL (attribution links, consent redirects, share targets, our own ?url=). */
const NESTED_PARAMS = ['u', 'url', 'continue', 'next', 'q', 'link', 'text'];

/**
 * A YouTube URL somewhere in a string: optional (possibly mangled) scheme - "https://", "https:/"
 * (a browser or proxy collapsed "//" in a path) or none - then a YouTube host that is not just the
 * tail of another name ("notyoutube.com" does not match).
 */
const YT_URL_IN_TEXT_RE =
  /(?:^|[^\w.-])((?:https?:\/{0,3})?(?:[a-z0-9-]+\.)*(?:youtube\.com|youtube-nocookie\.com|youtu\.be|ytimg\.com)(?=[/?#:]|$)[^\s<>"'`]*)/gi;

const MAX_DEPTH = 4;

export function isValidVideoId(value: string): boolean {
  return ID_RE.test(value);
}

/**
 * Returns the 11-character video ID contained in `input`, or null.
 *
 * Accepts raw IDs and every common link form (watch?v=, youtu.be/, shorts/, live/, embed/, v/, e/,
 * m./music./www., youtube-nocookie.com, with or without scheme, with extra params like si=, t=, list=),
 * attribution/consent links with a nested URL, text that contains a link, a YouTube URL embedded in
 * another URL (e.g. `https://our.app/https:/www.youtube.com/watch?v=ID`, where the browser collapsed
 * "//" and the query string now belongs to our URL), and paths relative to youtube.com
 * (`/watch?v=ID`, `/shorts/ID`, `/ID`, `?v=ID`) - so for "youtube.com replaced by our host" pass
 * `location.pathname + location.search + location.hash`. Other hosts (`example.com/watch?v=ID`) are rejected.
 */
export function parseYouTubeVideoId(input: string): string | null {
  return parse(input, 0);
}

function parse(input: unknown, depth: number): string | null {
  if (typeof input !== 'string' || depth > MAX_DEPTH) return null;
  const text = input.trim().replace(/^[<("'\s]+|[>)"'\s]+$/g, '');
  if (!text) return null;
  if (ID_RE.test(text)) return text;

  const candidates = [text];
  const decoded = safeDecode(text);
  if (decoded !== text) candidates.push(decoded);

  // 1. A YouTube URL anywhere in the input (also nested in another URL's path or query).
  for (const candidate of candidates) {
    for (const match of candidate.matchAll(YT_URL_IN_TEXT_RE)) {
      const raw = (match[1] ?? '').replace(/[.,;:!?)\]}*]+$/, ''); // trailing sentence punctuation
      const url = tryUrl(`https://${raw.replace(/^https?:\/*/i, '')}`);
      const id = url && idFromUrl(url, depth);
      if (id) return id;
    }
  }

  // 2. A path relative to youtube.com: "/watch?v=ID", "shorts/ID", "/ID", "?v=ID", "#/watch?v=ID".
  for (const candidate of candidates) {
    const relative = /^[/?#]/.test(candidate)
      ? candidate
      : /^(?:watch|shorts|live|embed)\b/i.test(candidate)
        ? `/${candidate}`
        : null;
    const url = relative !== null ? tryUrl(relative, 'https://www.youtube.com') : null;
    const id = url && idFromUrl(url, depth);
    if (id) return id;
  }
  return null;
}

function idFromUrl(url: URL, depth: number): string | null {
  const host = url.hostname.toLowerCase();
  const segments = url.pathname.split('/').filter(Boolean).map(safeDecode);
  const [first, second] = segments;

  // youtu.be/<id>
  if (host === 'youtu.be' && first) return leadingId(first);

  // /shorts/<id>, /live/<id>, /embed/<id>, /v/<id>, /watch/<id>, ...
  if (first && second && ID_PATH_PREFIXES.has(first.toLowerCase())) {
    const id = leadingId(second);
    if (id) return id;
  }

  // ?v=<id> (also "?v=<nested url>")
  const v = url.searchParams.get('v');
  if (v) {
    const id = leadingId(v.trim()) ?? parse(v, depth + 1);
    if (id) return id;
  }

  // Nested links: attribution_link?u=/watch?v=..., consent.youtube.com/...?continue=..., our ?url=...
  for (const name of NESTED_PARAMS) {
    const value = url.searchParams.get(name);
    const id = value ? parse(value, depth + 1) : null;
    if (id) return id;
  }

  // Old mobile links: m.youtube.com/#/watch?v=<id>; hash routers: our.app/#/watch?v=<id>
  if (url.hash.length > 1) {
    const id = parse(url.hash.slice(1), depth + 1);
    if (id) return id;
  }

  // youtube.com/<id> (rare short form) or a relative "/<id>"
  if (segments.length === 1 && first && ID_RE.test(first) && !NOT_IDS.has(first)) return first;
  return null;
}

function leadingId(value: string): string | null {
  const id = LEADING_ID_RE.exec(value)?.[1];
  return id && !NOT_IDS.has(id) ? id : null;
}

function tryUrl(value: string, base?: string): URL | null {
  try {
    return new URL(value, base);
  } catch {
    return null;
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
